import {
  Button,
  Checkbox,
  Input,
  Progress,
  Select,
  Table,
  Tooltip,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  FileTextOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BillItemChecklistEntry,
  BillRequiredItem,
  ClaimExtraDocument,
} from "./BillRequiredDocumentModal";
import { errorMessage } from "../../utils/notificationUtils";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/** Balanced scale for readable fonts without oversized spacing at 100% */
const S = 1.12;
const px = (n: number) => Math.round(n * S);

/** Modal uses z-index 10000+ — tooltip must sit above scaled app layers */
const TOOLTIP_PROPS = {
  placement: "topLeft" as const,
  mouseEnterDelay: 0.05,
  destroyTooltipOnHide: true,
  getPopupContainer: () => document.body,
  overlayClassName: "docs-required-tooltip",
  zIndex: 100000001,
  overlayStyle: { maxWidth: 520 },
  overlayInnerStyle: {
    // Do not set absolute fontSize here — app content is scale(0.67)
    // and body-portaled tooltips rely on .ant-tooltip-inner { font-size: 67% }
    padding: "8px 12px",
    wordBreak: "break-word" as const,
    whiteSpace: "normal" as const,
  },
};

/** Select menus also need to escape modal overflow + sit above z-index 10000 */
const SELECT_DROPDOWN_PROPS = {
  getPopupContainer: () => document.body,
  popupClassName: "docs-required-select-dropdown",
  dropdownClassName: "docs-required-select-dropdown",
  dropdownStyle: { zIndex: 100000001 },
};

export type DocumentsRequiredPatientInfo = {
  patientName?: string;
  uhid?: string;
  caseId?: string | number;
  hospitalName?: string;
  admissionDate?: string;
  dischargeDate?: string;
};

type SupportingDocumentChecklistModalProps = {
  visible?: boolean;
  inlineMode?: boolean;
  documents: any[];
  claimExtraDocuments: ClaimExtraDocument[];
  portalSubmission: boolean;
  patientInfo?: DocumentsRequiredPatientInfo;
  onCancel: () => void;
  onFileSelect: (
    doc: any,
    checkedItems: BillItemChecklistEntry[],
    file: File,
    allUploads?: Array<{ doc: any; checkedItems: BillItemChecklistEntry[] }>,
  ) => void;
  onView: (doc: any, documentUrl?: string) => void;
  onDownload?: (fileName: string, documentUrl: string) => void;
  onDelete?: (doc: any, documentUrl?: string) => void;
};

type RowStatus = "pending" | "uploaded" | "missing" | "rejected";

export type ChecklistRow = {
  rowKey: string;
  serialNo: number;
  documentId: number | string;
  documentLabel: string;
  itemName: string;
  itemDate?: string;
  itemCategory?: string;
  qty: number;
  dateLabel: string;
  dateValue: string;
  status: RowStatus;
  attachedFileName?: string;
  attachedFileUrl?: string;
  required: boolean;
  sourceDoc: any;
};

const COLORS = {
  bg: "#EEF2F7",
  card: "#FFFFFF",
  title: "#004c8f",
  subtitle: "#8B9CB3",
  label: "#A0AEC0",
  text: "#004c8f",
  textMuted: "#004c8f",
  border: "#E2E8F0",
  primary: "#004c8f",
  primarySoft: "#B8D0E8",
  patientName: "#004c8f",
  progressTrack: "#E2E8F0",
};

const STATUS_META: Record<
  RowStatus,
  { label: string; bg: string; color: string; icon: React.ReactNode }
> = {
  pending: {
    label: "Pending",
    bg: "#E8F1FF",
    color: "#2B6CB0",
    icon: <ClockCircleOutlined />,
  },
  uploaded: {
    label: "Uploaded",
    bg: "#E8F8EF",
    color: "#1F9D55",
    icon: <CheckCircleOutlined />,
  },
  missing: {
    label: "Missing",
    bg: "#FFF1E6",
    color: "#DD6B20",
    icon: <ExclamationCircleOutlined />,
  },
  rejected: {
    label: "Rejected",
    bg: "#FEECEC",
    color: "#E53E3E",
    icon: <CloseCircleOutlined />,
  },
};

const ITEM_DATE_FORMATS = [
  "DD/MM/YYYY",
  "DD-MM-YYYY",
  "YYYY-MM-DD",
  "D MMM YYYY",
  "D MMM",
];

const parseItemDate = (value?: string) => {
  if (!value) return null;
  for (const format of ITEM_DATE_FORMATS) {
    const parsed = dayjs(value, format, true);
    if (parsed.isValid()) return parsed;
  }
  const fallback = dayjs(value);
  return fallback.isValid() ? fallback : null;
};

const formatShortDate = (value?: string) => {
  if (!value) return "—";
  const parsed = parseItemDate(value);
  return parsed ? parsed.format("D MMM") : value;
};

const formatFullDate = (value?: string) => {
  if (!value) return "—";
  const parsed = parseItemDate(value);
  return parsed ? parsed.format("D MMM YYYY") : value;
};

const itemEntryKey = (itemName: string, itemDate?: string) =>
  `${itemName}::${itemDate || ""}`;

const getDocumentItems = (doc: any): BillRequiredItem[] => {
  if (doc?.items?.length) {
    return doc.items.map((item: BillRequiredItem) => ({
      itemName: item.itemName,
      itemDate: item.itemDate || "",
      itemCategory: item.itemCategory || "",
    }));
  }
  if (doc?.itemNames?.length) {
    const fallbackDate = doc?.itemDate || doc?.date || "";
    return doc.itemNames.map((itemName: string) => ({
      itemName,
      itemDate: fallbackDate,
      itemCategory: "",
    }));
  }
  return [{ itemName: "—", itemDate: "", itemCategory: "" }];
};

const getFileNameFromUrl = (url?: string, fallback?: string) => {
  if (url) {
    try {
      const path = url.split("?")[0];
      const parts = path.split("/");
      const raw = parts[parts.length - 1] || "";
      const decoded = decodeURIComponent(raw);
      // Prefer real file name from URL (e.g. consent_form.pdf), not document label
      if (decoded && /\.[a-z0-9]+$/i.test(decoded)) {
        return decoded;
      }
      if (decoded) return decoded;
    } catch {
      // fall through
    }
  }
  return fallback || undefined;
};

const EllipsisWithTooltip = ({
  text,
  maxWidth,
  style,
}: {
  text: string;
  maxWidth?: number | string;
  style?: React.CSSProperties;
}) => (
  <Tooltip title={text || undefined} {...TOOLTIP_PROPS}>
    <span
      style={{
        display: "inline-block",
        maxWidth: maxWidth ?? "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        verticalAlign: "bottom",
        cursor: "default",
        ...style,
      }}
    >
      {text}
    </span>
  </Tooltip>
);

const StatusPill = ({ status }: { status: RowStatus }) => {
  const meta = STATUS_META[status];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: px(3),
        background: meta.bg,
        color: meta.color,
        borderRadius: 999,
        padding: `${px(2)}px ${px(8)}px`,
        fontSize: px(10),
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {meta.icon}
      {meta.label}
    </span>
  );
};

const HEADER_BG = "#004c8f";
const HEADER_COLOR = "#ffffff";
const DATA_BLUE = "#004c8f";

const SupportingDocumentChecklistModal: React.FC<
  SupportingDocumentChecklistModalProps
> = ({
  visible = true,
  inlineMode = false,
  documents,
  claimExtraDocuments,
  portalSubmission,
  patientInfo,
  onCancel,
  onFileSelect,
  onView,
  onDownload,
  onDelete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadRef = useRef<
    Array<{ doc: any; checkedItems: BillItemChecklistEntry[] }> | null
  >(null);

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");

  const rows: ChecklistRow[] = useMemo(() => {
    const flattened: ChecklistRow[] = [];

    const findUploadForItem = (
      documentLabel: string,
      itemName: string,
      itemDate?: string,
    ) => {
      const matches = claimExtraDocuments?.filter((entry) => {
        const sameType =
          entry?.documentLabelType?.toUpperCase() ===
            documentLabel?.toUpperCase() ||
          entry?.documentName?.toUpperCase() === documentLabel?.toUpperCase();
        if (!sameType) return false;
        return entry?.itemNames?.some((item) => {
          if (item.itemName !== itemName || !item.checked) return false;
          if (item.itemDate && itemDate) {
            return item.itemDate === itemDate;
          }
          return true;
        });
      });
      return matches?.[matches.length - 1];
    };

    documents?.forEach((doc) => {
      const items = getDocumentItems(doc);
      const fallbackDate = patientInfo?.admissionDate || "";

      items.forEach((item, index: number) => {
        const itemName = item.itemName;
        const dateRaw = item.itemDate || fallbackDate;
        const upload = findUploadForItem(
          doc?.label || "",
          itemName,
          item.itemDate,
        );
        const hasUpload = Boolean(upload?.documentUrl);
        const status: RowStatus = hasUpload ? "uploaded" : "missing";

        flattened.push({
          rowKey: `${doc?.id ?? doc?.label}::${itemName}::${item.itemDate || dateRaw}::${index}`,
          serialNo: flattened.length + 1,
          documentId: doc?.id ?? doc?.label,
          documentLabel: doc?.label || doc?.documentName || "Document",
          itemName,
          itemDate: item.itemDate || undefined,
          itemCategory: item.itemCategory || undefined,
          qty: 1,
          dateLabel: formatShortDate(dateRaw),
          dateValue: dateRaw || "unknown",
          status,
          attachedFileName: upload?.documentName
            ? upload?.documentName
            : hasUpload
              ? getFileNameFromUrl(upload?.documentUrl, upload?.documentName)
              : undefined,
          attachedFileUrl: upload?.documentUrl,
          required: Boolean(doc?.required),
          sourceDoc: doc,
        });
      });
    });

    return flattened;
  }, [documents, claimExtraDocuments, patientInfo?.admissionDate]);

  useEffect(() => {
    if (!visible) {
      setSelectedRowKeys([]);
      setSearchText("");
      setStatusFilter("all");
      setDateFilter("all");
      setTypeFilter("all");
    }
  }, [visible]);

  const pendingCount = rows.filter((row) => row.status === "missing").length;

  const dateOptions = useMemo(() => {
    const unique = Array.from(
      new Set(rows.map((row) => row.dateLabel).filter((d) => d && d !== "—")),
    );
    return unique;
  }, [rows]);

  const typeOptions = useMemo(() => {
    const unique = Array.from(
      new Set(rows.map((row) => row.documentLabel).filter(Boolean)),
    );
    return unique;
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesSearch =
        !searchText ||
        row.itemName.toLowerCase().includes(searchText.toLowerCase()) ||
        row.documentLabel.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || row.status === statusFilter;
      const matchesDate = dateFilter === "all" || row.dateLabel === dateFilter;
      const matchesType =
        typeFilter === "all" || row.documentLabel === typeFilter;
      return matchesSearch && matchesStatus && matchesDate && matchesType;
    });
  }, [rows, searchText, statusFilter, dateFilter, typeFilter]);

  const uploadedCount = rows.filter((row) => row.status === "uploaded").length;
  const totalCount = rows.length;
  const progressPercent = totalCount
    ? Math.round((uploadedCount / totalCount) * 100)
    : 0;

  const selectedRows = rows.filter((row) =>
    selectedRowKeys.includes(row.rowKey),
  );
  const canCommonUpload = selectedRows.length > 1 && !portalSubmission;

  const filteredRowKeys = filteredRows.map((row) => row.rowKey);
  const allFilteredSelected =
    filteredRowKeys.length > 0 &&
    filteredRowKeys.every((key) => selectedRowKeys.includes(key));
  const someFilteredSelected =
    filteredRowKeys.some((key) => selectedRowKeys.includes(key)) &&
    !allFilteredSelected;

  const handleSelectAll = (checked: boolean) => {
    if (portalSubmission) return;

    if (!checked) {
      setSelectedRowKeys((prev) =>
        prev.filter((key) => !filteredRowKeys.includes(key)),
      );
      return;
    }

    setSelectedRowKeys((prev) =>
      Array.from(new Set([...prev, ...filteredRowKeys])),
    );
  };

  const handleRowCheck = (row: ChecklistRow, checked: boolean) => {
    if (portalSubmission) return;

    if (!checked) {
      setSelectedRowKeys((prev) => prev.filter((key) => key !== row.rowKey));
      return;
    }

    setSelectedRowKeys((prev) => [...prev, row.rowKey]);
  };

  const buildUploadTargets = (targetRows: ChecklistRow[]) => {
    const rowsByDocKey = new Map<string, ChecklistRow[]>();

    targetRows.forEach((row) => {
      const docKey = String(
        row.sourceDoc?.labelId ?? row.sourceDoc?.id ?? row.documentLabel,
      );
      const existing = rowsByDocKey.get(docKey) || [];
      existing.push(row);
      rowsByDocKey.set(docKey, existing);
    });

    const uploads: Array<{ doc: any; checkedItems: BillItemChecklistEntry[] }> =
      [];

    rowsByDocKey.forEach((groupedRows) => {
      const doc = groupedRows[0].sourceDoc;
      const selectedKeys = new Set(
        groupedRows.map((row) => itemEntryKey(row.itemName, row.itemDate)),
      );
      const checkedItems: BillItemChecklistEntry[] = getDocumentItems(doc).map(
        (item) => ({
          itemName: item.itemName,
          itemDate: item.itemDate || undefined,
          checked: selectedKeys.has(itemEntryKey(item.itemName, item.itemDate)),
        }),
      );

      if (checkedItems.some((entry) => entry.checked)) {
        uploads.push({ doc, checkedItems });
      }
    });

    return uploads;
  };

  const triggerUpload = (targetRows: ChecklistRow[]) => {
    if (!targetRows.length || portalSubmission) return;

    const uploads = buildUploadTargets(targetRows);
    if (!uploads.length) {
      errorMessage("Please select at least one bill item before uploading");
      return;
    }

    pendingUploadRef.current = uploads;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    const uploads = pendingUploadRef.current;
    if (file && uploads?.length) {
      onFileSelect(
        uploads[0].doc,
        uploads[0].checkedItems,
        file,
        uploads.length > 1 ? uploads : undefined,
      );
      setSelectedRowKeys([]);
    }
    pendingUploadRef.current = null;
    event.target.value = "";
  };

  const controlHeight = px(30);
  const controlFont = px(11);

  const columns = [
    {
      title: (
        <Checkbox
          className="docs-select-all"
          checked={allFilteredSelected}
          indeterminate={someFilteredSelected}
          disabled={portalSubmission || !filteredRowKeys.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: "select",
      width: px(36),
      onHeaderCell: () => ({
        style: {
          background: HEADER_BG,
          color: HEADER_COLOR,
          // fontWeight: 700,
        },
      }),
      render: (_: unknown, record: ChecklistRow) => {
        const isChecked = selectedRowKeys.includes(record.rowKey);
        return (
          <Checkbox
            checked={isChecked}
            disabled={portalSubmission}
            onChange={(e) => handleRowCheck(record, e.target.checked)}
          />
        );
      },
    },
    {
      title: "S.NO",
      dataIndex: "serialNo",
      key: "serialNo",
      width: px(48),
      align: "center" as const,
      onHeaderCell: () => ({
        style: {
          background: HEADER_BG,
          color: HEADER_COLOR,
          // fontWeight: 700,
        },
      }),
      render: (serialNo: number) => (
        <span style={{ color: DATA_BLUE, fontSize: px(11), fontWeight: 600 }}>
          {serialNo}
        </span>
      ),
    },
    {
      title: "DATE",
      dataIndex: "dateLabel",
      key: "date",
      width: px(70),
      onHeaderCell: () => ({
        style: {
          background: HEADER_BG,
          color: HEADER_COLOR,
          // fontWeight: 700,
        },
      }),
      render: (value: string) => (
        <span style={{ color: DATA_BLUE, fontSize: px(11), fontWeight: 600 }}>
          {value}
        </span>
      ),
    },
    {
      title: "CATEGORY",
      dataIndex: "itemCategory",
      key: "itemCategory",
      width: px(120),
      onHeaderCell: () => ({
        style: {
          background: HEADER_BG,
          color: HEADER_COLOR,
          // fontWeight: 700,
        },
      }),
      render: (itemCategory?: string) => (
        <EllipsisWithTooltip
          text={itemCategory || "—"}
          maxWidth={px(90)}
          style={{ color: DATA_BLUE, fontSize: px(11), fontWeight: 600 }}
        />
      ),
    },
    {
      title: "BILL ITEM",
      dataIndex: "itemName",
      key: "item",
      ellipsis: false,
      onHeaderCell: () => ({
        style: {
          background: HEADER_BG,
          color: HEADER_COLOR,
          // fontWeight: 700,
        },
      }),
      render: (itemName: string, record: ChecklistRow) => (
        <Tooltip
          title={itemName}
          {...TOOLTIP_PROPS}
          overlayClassName="docs-required-tooltip docs-required-item-tooltip"
        >
          <span
            style={{
              color: DATA_BLUE,
              fontSize: px(12),
              fontWeight: 700,
              lineHeight: 1.35,
              maxWidth: "100%",
              cursor: "default",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical" as const,
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {itemName}
            {record.required ? (
              <span style={{ color: "#E53E3E", marginLeft: 3 }}>*</span>
            ) : null}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "SUPPORTING",
      dataIndex: "documentLabel",
      key: "documentType",
      width: px(120),
      onHeaderCell: () => ({
        style: {
          background: HEADER_BG,
          color: HEADER_COLOR,
          // fontWeight: 700,
        },
      }),
      render: (label: string) => (
        <EllipsisWithTooltip
          text={label}
          maxWidth={px(110)}
          style={{
            background: "#f5f5f5",
            color: DATA_BLUE,
            borderRadius: 999,
            padding: `${px(2)}px ${px(8)}px`,
            fontSize: px(10),
            fontWeight: 600,
          }}
        />
      ),
    },
    // {
    //   title: "STATUS",
    //   dataIndex: "status",
    //   key: "status",
    //   width: px(100),
    //   onHeaderCell: () => ({
    //     style: {
    //       background: HEADER_BG,
    //       color: HEADER_COLOR,
    //       fontWeight: 700,
    //     },
    //   }),
    //   render: (status: RowStatus) => <StatusPill status={status} />,
    // },
    {
      title: "ATTACHED FILE",
      key: "attachedFile",
      width: px(180),
      onHeaderCell: () => ({
        style: {
          background: HEADER_BG,
          color: HEADER_COLOR,
          // fontWeight: 700,
        },
      }),
      render: (_: unknown, record: ChecklistRow) => {
        if (!record.attachedFileName || !record.attachedFileUrl) {
          return <span style={{ color: "#CBD5E1", fontSize: px(11) }}>—</span>;
        }

        const iconBtnStyle: React.CSSProperties = {
          color: DATA_BLUE,
          fontSize: px(12),
          cursor: portalSubmission ? "not-allowed" : "pointer",
          opacity: portalSubmission ? 0.5 : 1,
        };

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: px(6),
              minWidth: 0,
            }}
          >
            <Tooltip title={record.attachedFileName} {...TOOLTIP_PROPS}>
              <button
                type="button"
                onClick={() =>
                  onView(record.sourceDoc, record.attachedFileUrl)
                }
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                  color: DATA_BLUE,
                  fontSize: px(11),
                  fontWeight: 600,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  textAlign: "left",
                }}
              >
                {record.attachedFileName}
              </button>
            </Tooltip>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: px(6),
                flexShrink: 0,
              }}
            >
              <Tooltip title="Preview" {...TOOLTIP_PROPS}>
                <EyeOutlined
                  style={iconBtnStyle}
                  onClick={() => {
                    if (portalSubmission) return;
                    onView(record.sourceDoc, record.attachedFileUrl);
                  }}
                />
              </Tooltip>
              <Tooltip title="Download" {...TOOLTIP_PROPS}>
                <DownloadOutlined
                  style={iconBtnStyle}
                  onClick={() => {
                    if (portalSubmission || !onDownload) return;
                    onDownload(
                      record.attachedFileName || "document",
                      record.attachedFileUrl!,
                    );
                  }}
                />
              </Tooltip>
              <Tooltip title="Delete" {...TOOLTIP_PROPS}>
                <DeleteOutlined
                  style={{ ...iconBtnStyle, color: "#F87171" }}
                  onClick={() => {
                    if (portalSubmission || !onDelete) return;
                    onDelete(record.sourceDoc, record.attachedFileUrl);
                  }}
                />
              </Tooltip>
            </div>
          </div>
        );
      },
    },
    {
      title: "ACTION",
      key: "action",
      width: px(100),
      onHeaderCell: () => ({
        style: {
          background: HEADER_BG,
          color: HEADER_COLOR,
          // fontWeight: 700,
        },
      }),
      render: (_: unknown, record: ChecklistRow) => {
        if (record.status === "uploaded") {
          return (
            <Button
              icon={<DeleteOutlined />}
              disabled={portalSubmission}
              onClick={() => {
                if (portalSubmission || !onDelete) return;
                onDelete(record.sourceDoc, record.attachedFileUrl);
              }}
              style={{
                height: px(26),
                fontSize: px(10),
                color: "#DD6B20",
                borderColor: "#F6AD55",
                background: "#fff",
                borderRadius: px(6),
                paddingInline: px(8),
              }}
            >
              Delete
            </Button>
          );
        }
        return (
          <Button
            icon={<UploadOutlined />}
            disabled={portalSubmission}
            onClick={() => triggerUpload([record])}
            style={{
              height: px(26),
              fontSize: px(10),
              color: DATA_BLUE,
              borderColor: DATA_BLUE,
              background: "#fff",
              borderRadius: px(6),
              paddingInline: px(8),
              fontWeight: 600,
            }}
          >
            Upload
          </Button>
        );
      },
    },
  ];

  const tableContent = (
    <>
      <style>
        {`
          .docs-required-modal .ant-table-thead > tr > th,
          .docs-required-inline .ant-table-thead > tr > th {
            background: ${HEADER_BG} !important;
            color: ${HEADER_COLOR} !important;
            font-size: ${px(9)}px !important;
            font-weight: 700 !important;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            border-bottom: 1px solid ${HEADER_BG} !important;
            padding: ${px(7)}px ${px(6)}px !important;
          }
          .docs-required-modal .ant-table-thead > tr > th::before,
          .docs-required-inline .ant-table-thead > tr > th::before {
            display: none !important;
          }
          .docs-required-modal .ant-table-tbody > tr > td,
          .docs-required-inline .ant-table-tbody > tr > td {
            padding: ${px(6)}px ${px(6)}px !important;
            border-bottom: 1px solid ${COLORS.border} !important;
            vertical-align: middle;
            font-size: ${px(11)}px !important;
            color: ${DATA_BLUE};
            font-weight: 600;
          }
          .docs-required-modal .ant-table-tbody > tr:nth-child(odd) > td,
          .docs-required-inline .ant-table-tbody > tr:nth-child(odd) > td {
            background: #f7f9fc !important;
          }
          .docs-required-modal .ant-checkbox-checked .ant-checkbox-inner,
          .docs-required-inline .ant-checkbox-checked .ant-checkbox-inner {
            background-color: ${HEADER_BG} !important;
            border-color: ${HEADER_BG} !important;
          }
          .docs-required-modal .docs-select-all .ant-checkbox-inner,
          .docs-required-inline .docs-select-all .ant-checkbox-inner {
            background-color: #ffffff !important;
            border-color: #ffffff !important;
          }
          .docs-required-modal .docs-select-all .ant-checkbox-checked .ant-checkbox-inner,
          .docs-required-inline .docs-select-all .ant-checkbox-checked .ant-checkbox-inner,
          .docs-required-modal .docs-select-all .ant-checkbox-indeterminate .ant-checkbox-inner,
          .docs-required-inline .docs-select-all .ant-checkbox-indeterminate .ant-checkbox-inner {
            background-color: #ffffff !important;
            border-color: #ffffff !important;
          }
          .docs-required-modal .docs-select-all .ant-checkbox-checked .ant-checkbox-inner::after,
          .docs-required-inline .docs-select-all .ant-checkbox-checked .ant-checkbox-inner::after {
            border-color: ${HEADER_BG} !important;
          }
          .docs-required-modal .docs-select-all .ant-checkbox-indeterminate .ant-checkbox-inner::after,
          .docs-required-inline .docs-select-all .ant-checkbox-indeterminate .ant-checkbox-inner::after {
            background-color: ${HEADER_BG} !important;
          }
          .docs-required-modal .ant-table,
          .docs-required-inline .ant-table {
            table-layout: fixed !important;
            width: 100% !important;
          }
          .docs-required-modal .ant-table-tbody > tr > td:nth-child(4),
          .docs-required-inline .ant-table-tbody > tr > td:nth-child(4),
          .docs-required-modal .ant-table-thead > tr > th:nth-child(4),
          .docs-required-inline .ant-table-thead > tr > th:nth-child(4) {
            width: auto !important;
          }
          .docs-required-modal .ant-table-tbody > tr:hover > td,
          .docs-required-inline .ant-table-tbody > tr:hover > td {
            background: #F8FAFC !important;
          }
          .docs-required-modal .docs-filter.ant-select .ant-select-selector,
          .docs-required-inline .docs-filter.ant-select .ant-select-selector {
            height: ${controlHeight}px !important;
            border-radius: ${px(8)}px !important;
            border-color: ${COLORS.border} !important;
            align-items: center;
            font-size: ${controlFont}px;
          }
          .docs-required-modal .docs-search.ant-input-affix-wrapper,
          .docs-required-inline .docs-search.ant-input-affix-wrapper {
            height: ${controlHeight}px;
            border-radius: ${px(8)}px;
            border-color: ${COLORS.border};
            font-size: ${controlFont}px;
          }
        `}
      </style>

      <div
        style={{
          padding: inlineMode ? `${px(10)}px ${px(12)}px 0` : `${px(14)}px ${px(16)}px 0`,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
          background: COLORS.bg,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: px(10),
            gap: px(12),
            flexShrink: 0,
            paddingRight: inlineMode ? 0 : px(20),
          }}
        >
          <div>
            <div
              style={{
                fontSize: inlineMode ? px(14) : px(18),
                fontWeight: 700,
                color: COLORS.title,
                lineHeight: 1.15,
              }}
            >
              Documents Required
            </div>
            <div
              style={{
                color: COLORS.subtitle,
                marginTop: px(2),
                fontSize: px(11),
              }}
            >
              Upload and assign documents to one or multiple required items.
            </div>
          </div>
          <div style={{ minWidth: px(140), textAlign: "right" }}>
            <div
              style={{
                fontSize: px(8),
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: COLORS.label,
                marginBottom: px(2),
              }}
            >
              CASE PROGRESS
            </div>
            <div
              style={{
                fontSize: px(13),
                fontWeight: 700,
                color: COLORS.title,
                marginBottom: px(4),
              }}
            >
              {uploadedCount}/{totalCount} Uploaded
            </div>
            <Progress
              percent={progressPercent}
              showInfo={false}
              strokeColor={COLORS.primary}
              trailColor={COLORS.progressTrack}
              size={px(6)}
              style={{ lineHeight: 1 }}
            />
          </div>
        </div>

        <div
          style={{
            background: COLORS.card,
            borderRadius: px(8),
            border: `1px solid ${COLORS.border}`,
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
            padding: `${px(8)}px ${px(8)}px 0`,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: px(6),
              marginBottom: px(8),
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Input
              allowClear
              className="docs-search"
              prefix={
                <SearchOutlined
                  style={{ color: COLORS.label, fontSize: px(13) }}
                />
              }
              placeholder="Search by item..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: px(220), flex: "1 1 auto", maxWidth: px(280) }}
            />
            <Select
              className="docs-filter"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: px(140) }}
              suffixIcon={<FilterOutlined style={{ color: COLORS.label }} />}
              {...SELECT_DROPDOWN_PROPS}
              options={[
                { value: "all", label: "All Status" },
                { value: "uploaded", label: "Uploaded" },
                { value: "missing", label: "Missing" },
              ]}
            />
            <Select
              className="docs-filter"
              value={dateFilter}
              onChange={setDateFilter}
              style={{ width: px(130) }}
              suffixIcon={<CalendarOutlined style={{ color: COLORS.label }} />}
              {...SELECT_DROPDOWN_PROPS}
              options={[
                { value: "all", label: "All Dates" },
                ...dateOptions.map((date) => ({ value: date, label: date })),
              ]}
            />
            <Select
              className="docs-filter"
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: px(180) }}
              suffixIcon={<FileTextOutlined style={{ color: COLORS.label }} />}
              placeholder="Document Type"
              {...SELECT_DROPDOWN_PROPS}
              options={[
                { value: "all", label: "All Document Types" },
                ...typeOptions.map((type) => ({ value: type, label: type })),
              ]}
            />
            <div style={{ flex: 1 }} />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canCommonUpload}
              onClick={() => triggerUpload(selectedRows)}
              style={{
                height: controlHeight,
                borderRadius: px(8),
                backgroundColor: canCommonUpload ? COLORS.primary : undefined,
                borderColor: canCommonUpload ? COLORS.primary : undefined,
                fontSize: controlFont,
                fontWeight: 600,
                paddingInline: px(14),
              }}
            >
              Upload Document
            </Button>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <Table
              columns={columns}
              dataSource={filteredRows}
              rowKey="rowKey"
              pagination={false}
              scroll={{ y: inlineMode ? 360 : "calc(100vh - 360px)" }}
              size="small"
              locale={{ emptyText: "No required documents found" }}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: px(8),
          padding: `${px(8)}px ${inlineMode ? px(12) : px(16)}px`,
          background: COLORS.card,
          borderTop: `1px solid ${COLORS.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: px(10),
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 0,
            flexWrap: "wrap",
          }}
        >
          <div style={{ paddingRight: px(12) }}>
            <span
              style={{
                fontWeight: 700,
                color: COLORS.title,
                fontSize: px(12),
              }}
            >
              {totalCount}
            </span>{" "}
            <span style={{ color: COLORS.subtitle, fontSize: px(11) }}>
              Documents Required
            </span>
          </div>
          <div
            style={{
              width: 1,
              height: px(14),
              background: COLORS.border,
              marginRight: px(12),
            }}
          />
          <div
            style={{
              paddingRight: px(12),
              display: "flex",
              alignItems: "center",
              gap: px(4),
            }}
          >
            <CheckCircleFilled
              style={{ color: "#22C55E", fontSize: px(12) }}
            />
            <span
              style={{
                fontWeight: 700,
                color: "#16A34A",
                fontSize: px(12),
              }}
            >
              {uploadedCount}
            </span>{" "}
            <span style={{ color: COLORS.subtitle, fontSize: px(11) }}>
              Uploaded
            </span>
          </div>
          <div
            style={{
              width: 1,
              height: px(14),
              background: COLORS.border,
              marginRight: px(12),
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: px(4) }}>
            <ClockCircleOutlined
              style={{ color: "#64748B", fontSize: px(12) }}
            />
            <span
              style={{
                fontWeight: 700,
                color: COLORS.title,
                fontSize: px(12),
              }}
            >
              {pendingCount}
            </span>{" "}
            <span style={{ color: COLORS.subtitle, fontSize: px(11) }}>
              Missing
            </span>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className={inlineMode ? "docs-required-inline" : "docs-required-modal"}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />
      {inlineMode ? (
        <div
          style={{
            background: COLORS.bg,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: px(12),
            border: `1px solid ${COLORS.border}`,
            marginTop: px(12),
          }}
        >
          {tableContent}
        </div>
      ) : (
        <div style={{ display: visible ? "block" : "none" }}>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.45)",
              zIndex: 1000,
            }}
            onClick={onCancel}
          />
          <div
            style={{
              position: "fixed",
              top: 20,
              left: "50%",
              transform: "translateX(-50%)",
              width: "96vw",
              maxWidth: 4000,
              height: "calc(100vh - 40px)",
              zIndex: 1001,
              background: COLORS.bg,
              borderRadius: px(12),
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 24px 48px rgba(15, 23, 42, 0.18)",
            }}
          >
            {tableContent}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportingDocumentChecklistModal;
